import { List } from "immutable";
import _ from "lodash";
import React from "react";
import { FormField, RadioOrCheckListField, SelectField, TextField } from "./";
import { ContactTypes } from "../../domain";
import { AddressData } from "../../forms/generic";
import { Country } from "../../models/country";
import { SelectItem } from "../../models/editors";
import { Utility } from "../../utils";
import { IComponentWithValidatedForm, ValidatedFormFieldBase, ValidatedInput, ValidatedSelect } from "../validation";
import { CountriesLoader } from "../reusable";

interface OwnProps {
    excludeAddressLines?: boolean;
    excludeAddressType?: boolean;
    excludePostalCode?: boolean;
}

interface CountryTemplateProps {
    code: string;
    countries: Country[];
    formComponent: IComponentWithValidatedForm;
    name: string;
}

export class AddressField extends ValidatedFormFieldBase<OwnProps, {}> {

    private _countries: List<Country> = List();

    public render() {
        // const label = this.getLabel(this.props.label);
        const addr: AddressData | undefined = this.getValue() as AddressData;
        const countryCode = addr && addr.countryCode ? (addr.countryCode as Country).isoCode : undefined;
        const countries = this._countries.toArray().map((c) => {
            return Utility.toReactSelectOption(c, c.name, c.isoCode);
        });
        const contactTypes: SelectItem[] = ContactTypes.Values.map((ct) => ({
            id: ct.id,
            selected: false,
            render: ct.name,
        }));

        const excludePostalCode = this.props.excludePostalCode === undefined
            ? false
            : this.props.excludePostalCode;

        let template = null;

        if (excludePostalCode)
            template = <CityState_Template code={countryCode || ""} countries={countries} formComponent={this.props.formComponent} name={this.props.name} />;
        else {
            switch (countryCode) {
                case "AU":
                case "CA":
                case "US":
                    template = <CityStatePostal_Template code={countryCode} countries={countries} formComponent={this.props.formComponent} name={this.props.name} />;
                    break;
                case "DE":
                case "FR":
                    template = <PostalCity_Template code={countryCode} countries={countries} formComponent={this.props.formComponent} name={this.props.name} />;
                    break;
                case "MX":
                    template = <PostalCityState_Template code={countryCode} countries={countries} formComponent={this.props.formComponent} name={this.props.name} />;
                    break;
                default:
                    template = <GenericCountryTemplate code={countryCode || ""} countries={countries} formComponent={this.props.formComponent} name={this.props.name} />;
                    break;
            }
        }

        const excludeAddressLines = this.props.excludeAddressLines === undefined
            ? false
            : this.props.excludeAddressLines;

        const addressLines = excludeAddressLines
            ? null
            : [
                <TextField key={0}
                    errors={this.props.formComponent.getFieldErrors(this.props.name + ".address1")}
                    formComponent={this.props.formComponent}
                    name={this.props.name + ".address1"}
                    label={this.getLabel(this.props.label)}
                    placeholder="Street address, P.O. Box, etc."
                />,
                <TextField key={1}
                    errors={this.props.formComponent.getFieldErrors(this.props.name + ".address2")}
                    formComponent={this.props.formComponent}
                    name={this.props.name + ".address2"}
                    label=""
                    placeholder="Apt, suite, unit, building, floor, etc."
                />,
                <TextField key={2}
                    errors={this.props.formComponent.getFieldErrors(this.props.name + ".address3")}
                    formComponent={this.props.formComponent}
                    name={this.props.name + ".address3"}
                    label=""
                    placeholder="Apt, suite, unit, building, floor, etc."
                />,
            ];

        const excludeAddressType = this.props.excludeAddressType === undefined
            ? false
            : this.props.excludeAddressType;

        const addressType = excludeAddressType
            ? null
            : <RadioOrCheckListField
                formComponent={this.props.formComponent}
                items={contactTypes}
                label={this.getLabel(this.props.label) + " type"}
                listCss="options-h"
                name={this.props.name + ".contactTypeId"}
                type="radio"
            />;

        return <div className="address-field">
            {addressLines}

            <CountriesLoader onUpdated={(countryList) => this._countries = countryList} />

            <SelectField
                errors={this.props.formComponent.getFieldErrors(this.props.name + ".countryCode")}
                formComponent={this.props.formComponent}
                name={this.props.name + ".countryCode"}
                label={excludeAddressLines ? this.getLabel(this.props.label) : ""}
                select={{
                    className: "txt-med",
                    clearable: false,
                    options: countries,
                    placeholder: "Select country...",
                }}
            />

            {template}

            {addressType}
        </div>;
    }
}

const GenericCountryTemplate: React.SFC<CountryTemplateProps> = (props) => {
    return (
        <div className="address-generic">
            <TextField
                errors={props.formComponent.getFieldErrors(props.name + ".city")}
                formComponent={props.formComponent}
                name={props.name + ".city"}
                label=""
                placeholder="City or municipality"
            />

            <TextField
                errors={props.formComponent.getFieldErrors(props.name + ".state")}
                formComponent={props.formComponent}
                name={props.name + ".state"}
                label=""
                placeholder="State or province"
            />

            <TextField
                errors={props.formComponent.getFieldErrors(props.name + ".postalCode")}
                formComponent={props.formComponent}
                name={props.name + ".postalCode"}
                label=""
                placeholder="Postal code"
            />
        </div>
    );
};

const CityStatePostal_Template: React.SFC<CountryTemplateProps> = (props) => {
    const addr: AddressData | undefined = props.formComponent.getFieldValue(props.name) as any as AddressData;
    const ctry = _.find(props.countries, (c) => c.isoCode === props.code);
    let errors = props.formComponent.getFieldErrors(props.name + ".city");
    errors = errors.concat(props.formComponent.getFieldErrors(props.name + ".state"));
    errors = errors.concat(props.formComponent.getFieldErrors(props.name + ".postalCode"));

    const states: SelectItem[] = !ctry
        ? []
        : _.orderBy(ctry.states, "code").map((s) => ({
            id: s.code,
            render: s.code,
            selected: false,
        }));

    let statePlaceholder: string | undefined;
    let zipPlaceholder: string | undefined;
    switch (props.code) {
        case "AU":
            statePlaceholder = "State/Territory";
            zipPlaceholder = "Postcode";
            break;
        case "CA":
            statePlaceholder = "Province";
            zipPlaceholder = "Postal code";
            break;
        default:
            statePlaceholder = "State...";
            zipPlaceholder = "Zip code";
            break;
    }

    return (
        <FormField {...props} errors={errors} label="" name="" required={false}>
            <div className="field-single-line">
                <p className="field-loc-city">
                    <ValidatedInput
                        formComponent={props.formComponent}
                        type="text"
                        id={Utility.toHtmlIdString(props.name + ".city")}
                        name={props.name + ".city"}
                        css="txt"
                        placeholder="City"
                        value={addr ? (addr.city || "") : ""}
                    />
                </p>
                <p className="field-loc-state">
                    <ValidatedSelect
                        css="txt"
                        formComponent={props.formComponent}
                        id={Utility.toHtmlIdString(props.name + ".state")}
                        items={states}
                        name={props.name + ".state"}
                        placeholder={statePlaceholder}
                        title={statePlaceholder}
                    />
                </p>
                <p className="field-loc-postal">
                    <ValidatedInput
                        formComponent={props.formComponent}
                        type="text"
                        id={Utility.toHtmlIdString(props.name + ".postalCode")}
                        name={props.name + ".postalCode"}
                        css="txt"
                        placeholder={zipPlaceholder}
                        value={addr ? (addr.postalCode || "") : ""}
                    />
                </p>
            </div>
        </FormField>
    );
};

const PostalCity_Template: React.SFC<CountryTemplateProps> = (props) => {
    const addr: AddressData | undefined = props.formComponent.getFieldValue(props.name) as any as AddressData;
    let errors = props.formComponent.getFieldErrors(props.name + ".city");
    errors = errors.concat(props.formComponent.getFieldErrors(props.name + ".state"));
    errors = errors.concat(props.formComponent.getFieldErrors(props.name + ".postalCode"));

    let zipPlaceholder: string | undefined;
    switch (props.code) {
        case "DE":
            zipPlaceholder = "PLZ";
            break;
        case "FR":
            zipPlaceholder = "Code postal";
            break;
    }

    return (
        <FormField {...props} errors={errors} label="" name="" required={false}>
            <div className="field-single-line postal-city">
                <p className="field-loc-postal">
                    <ValidatedInput
                        formComponent={props.formComponent}
                        type="text"
                        id={Utility.toHtmlIdString(props.name + ".postalCode")}
                        name={props.name + ".postalCode"}
                        css="txt"
                        placeholder={zipPlaceholder}
                        value={addr ? (addr.postalCode || "") : ""}
                    />
                </p>
                <p className="field-loc-city">
                    <ValidatedInput
                        formComponent={props.formComponent}
                        type="text"
                        id={Utility.toHtmlIdString(props.name + ".city")}
                        name={props.name + ".city"}
                        css="txt"
                        placeholder="City or region"
                        value={addr ? (addr.city || "") : ""}
                    />
                </p>
            </div>
        </FormField>
    );
};

const PostalCityState_Template: React.SFC<CountryTemplateProps> = (props) => {
    const addr: AddressData | undefined = props.formComponent.getFieldValue(props.name) as any as AddressData;
    const ctry = _.find(props.countries, (c) => c.isoCode === props.code);
    let errors = props.formComponent.getFieldErrors(props.name + ".city");
    errors = errors.concat(props.formComponent.getFieldErrors(props.name + ".state"));
    errors = errors.concat(props.formComponent.getFieldErrors(props.name + ".postalCode"));

    const states: SelectItem[] = !ctry
        ? []
        : _.orderBy(ctry.states, "code").map((s) => ({
            id: s.code,
            render: s.code,
            selected: false,
        }));

    let statePlaceholder: string | undefined;
    let zipPlaceholder: string | undefined;
    switch (props.code) {
        case "MX":
        default:
            statePlaceholder = "Estado...";
            zipPlaceholder = "Código postal";
            break;
    }

    return (
        <FormField {...props} errors={errors} label="" name="" required={false}>
            <div className="field-single-line postal-city-state">
                <p className="field-loc-postal">
                    <ValidatedInput
                        formComponent={props.formComponent}
                        type="text"
                        id={Utility.toHtmlIdString(props.name + ".postalCode")}
                        name={props.name + ".postalCode"}
                        css="txt"
                        placeholder={zipPlaceholder}
                        value={addr ? (addr.postalCode || "") : ""}
                    />
                </p>
                <p className="field-loc-city">
                    <ValidatedInput
                        formComponent={props.formComponent}
                        type="text"
                        id={Utility.toHtmlIdString(props.name + ".city")}
                        name={props.name + ".city"}
                        css="txt"
                        placeholder="City"
                        value={addr ? (addr.city || "") : ""}
                    />
                </p>
                <p className="field-loc-state">
                    <ValidatedSelect
                        css="txt"
                        formComponent={props.formComponent}
                        id={Utility.toHtmlIdString(props.name + ".state")}
                        items={states}
                        name={props.name + ".state"}
                        placeholder={statePlaceholder}
                        title={statePlaceholder}
                    />
                </p>
            </div>
        </FormField>
    );
};

const CityState_Template: React.SFC<CountryTemplateProps> = (props) => {
    const addr: AddressData | undefined = props.formComponent.getFieldValue(props.name) as any as AddressData;
    const ctry = _.find(props.countries, (c) => c.isoCode === props.code);
    let errors = props.formComponent.getFieldErrors(props.name + ".city");
    errors = errors.concat(props.formComponent.getFieldErrors(props.name + ".state"));

    const states: SelectItem[] = !ctry
        ? []
        : _.orderBy(ctry.states, "code").map((s) => ({
            id: s.code,
            render: s.code,
            selected: false,
        }));

    let statePlaceholder: string | undefined;
    switch (props.code) {
        case "AU":
            statePlaceholder = "State/Territory";
            break;
        case "CA":
            statePlaceholder = "Province";
            break;
        case "MX":
            statePlaceholder = "Estado...";
            break;
        case "US":
        default:
            statePlaceholder = "State...";
            break;
    }

    if (!states.length) {
        return <div className="address-generic">
            <TextField
                errors={props.formComponent.getFieldErrors(props.name + ".city")}
                formComponent={props.formComponent}
                name={props.name + ".city"}
                label=""
                placeholder="City or municipality"
            />

            <TextField
                errors={props.formComponent.getFieldErrors(props.name + ".state")}
                formComponent={props.formComponent}
                name={props.name + ".state"}
                label=""
                placeholder="State or province"
            />
        </div>;
    }
    else {
        return <FormField {...props} errors={errors} label="" name="" required={false}>
            <div className="field-single-line city-state">
                <p className="field-loc-city">
                    <ValidatedInput
                        formComponent={props.formComponent}
                        type="text"
                        id={Utility.toHtmlIdString(props.name + ".city")}
                        name={props.name + ".city"}
                        css="txt"
                        placeholder="City"
                        value={addr ? (addr.city || "") : ""}
                    />
                </p>
                <p className="field-loc-state">
                    <ValidatedSelect
                        css="txt"
                        formComponent={props.formComponent}
                        id={Utility.toHtmlIdString(props.name + ".state")}
                        items={states}
                        name={props.name + ".state"}
                        placeholder={statePlaceholder}
                        title={statePlaceholder}
                    />
                </p>
            </div>
        </FormField>;
    }
};



// WEBPACK FOOTER //
// ./src/components/editors/AddressField.tsx